/**
 * DERNIÈRE DEMANDE par widget — seule la réponse à la requête la plus RÉCEMMENT DEMANDÉE s'applique.
 *
 * Deux filtres touchés à la suite lancent deux requêtes pour le même widget. Si la première
 * répond APRÈS la seconde, elle ne doit rien écraser : sinon le widget affiche les chiffres d'un
 * filtrage qui n'a plus cours, sans le moindre signe. En production, 100 rafales sur 358 du
 * dashboard Contrôle interne partent moins de 10 s après la précédente (journaux du 07/10/2026).
 *
 * Ce module remplace la garde par date (`newWidgetDataShouldOverideThePreviousOne` sur l'en-tête
 * `x-request-date`), qui ne protégeait de rien : l'ETL n'envoie pas cet en-tête, et une date
 * absente laissait passer toute réponse — la DERNIÈRE ARRIVÉE gagnait.
 *
 * L'identifiant suivi est le `requestId` que la saga tire pour chaque requête, avec le CONTENU
 * de la requête (`cle`, son corps sérialisé).
 *
 * ⚠️ Deux règles, pas une :
 *  - `estApplicable` (écrire les DONNÉES) accepte aussi une réponse dont le contenu de requête est
 *    IDENTIQUE à celui de la dernière demande : elle répond exactement à la question en cours. Cas
 *    réel : à l'ouverture d'une page, chaque widget envoie la même requête au montage puis, 3 à 6 s
 *    plus tard, à l'arrivée des champs du cube. Sur un dashboard lent, la première réponse arrive
 *    après le second envoi : la jeter laissait le widget en chargement plusieurs secondes de trop.
 *  - `estLaDerniere` (relancer, signaler une ERREUR) reste stricte : si une demande identique plus
 *    récente est en vol, c'est elle qui apportera la réponse ; l'échec de l'ancienne est muet.
 *
 * Module volontairement LÉGER, sans import : testable sans charger le graphe des widgets.
 */
export const creerSuiviDemandes = () => {
  const dernieres = new Map()
  return {
    /** À appeler AVANT d'envoyer la requête : elle devient la dernière demande du widget. */
    noter: (widgetId, requestId, cle = null) => {
      dernieres.set(widgetId, { requestId, cle })
    },
    /** Aucune demande plus récente n'est partie pour ce widget (relance, erreur). */
    estLaDerniere: (widgetId, requestId) => dernieres.get(widgetId)?.requestId === requestId,
    /** La réponse peut être APPLIQUÉE : dernière demande, ou même contenu que la dernière demande. */
    estApplicable: (widgetId, requestId, cle) => {
      const derniere = dernieres.get(widgetId)
      if (!derniere) return false
      return derniere.requestId === requestId || (cle != null && derniere.cle === cle)
    },
  }
}

/** Suivi partagé par la saga des données de widgets. */
export const suiviDemandesWidgets = creerSuiviDemandes()
